// Default content for all landing pages - used to seed CMS database
import { themes } from "@/components/landing/themes";

export interface LandingPageContent {
  hero: {
    title: string;
    subtitle: string;
    cta_primary: string;
    cta_secondary: string;
    highlight: string;
  };
  pain_solution: {
    pain_title: string;
    pains: string[];
    pain_subtitle: string;
    solution_title: string;
    solutions: string[];
  };
  benefits: {
    title: string;
    items: string[];
  };
  how_it_works: {
    title: string;
    steps: string[];
  };
  social_proof: {
    title: string;
    testimonials: Array<{
      name: string;
      role: string;
      content: string;
      rating: number;
    }>;
  };
  pricing: {
    title: string;
    subtitle: string;
    plans: Array<{
      name: string;
      price: string;
      originalPrice?: string;
      description: string;
      urgencyText?: string;
      features: string[];
      popular?: boolean;
    }>;
  };
  faq: {
    title: string;
    items: Array<{ question: string; answer: string }>;
  };
  cta_final: {
    title: string;
    subtitle: string;
    button: string;
  };
  fomo: {
    activity_messages: string[];
    offer_bar: {
      text: string;
      subtext: string;
      cta_text: string;
    };
    exit_popup: {
      title: string;
      subtitle: string;
      cta_text: string;
      discount_code: string;
    };
  };
  meta: {
    title: string;
    description: string;
  };
  theme: {
    primary: string;
    secondary: string;
    accent: string;
    gradient: string;
  };
  view_count_range: [number, number];
}

export const LANDING_PAGE_DEFAULTS: Record<string, LandingPageContent> = {
  moda: {
    hero: {
      title: "Pare de Perder Vendas. Transforme Sua Loja em uma Máquina de Lucro.",
      subtitle: "Controle de estoque, PDV rápido, clientes fiéis e relatórios em um único sistema fácil de usar.",
      cta_primary: "Criar minha conta grátis agora",
      cta_secondary: "Ver como funciona",
      highlight: "Comece em menos de 60 segundos. Sem precisar de cartão.",
    },
    pain_solution: {
      pain_title: "Sua loja está perdendo dinheiro sem você perceber.",
      pains: [
        "Você não sabe exatamente o que mais vende e o que só ocupa espaço.",
        "Clientes compram uma vez e somem, porque você não tem um controle de relacionamento.",
        "O estoque vive bagunçado e você descobre falta de produto só quando o cliente pede.",
        "As vendas ficam espalhadas em caderno, planilha e maquininhas.",
      ],
      pain_subtitle: "Não é falta de esforço. É falta de ferramenta certa.",
      solution_title: "Com o nosso sistema, sua loja fica no controle de tudo.",
      solutions: [
        "Estoque em tempo real, com alertas de reposição.",
        "Histórico de compras de cada cliente, pronto pra campanhas de volta às compras.",
        "PDV simples e rápido para registrar vendas em segundos.",
        "Relatórios claros de faturamento, ticket médio e produtos mais vendidos.",
      ],
    },
    benefits: {
      title: "Feito para quem vive de moda, não de planilha.",
      items: [
        "Veja o que entra e sai do estoque, sem sustos.",
        "Crie promoções certeiras com base em dados reais.",
        "Monte campanhas de WhatsApp e e-mail em poucos cliques.",
        "Descubra quem são seus melhores clientes e como vender mais para eles.",
      ],
    },
    how_it_works: {
      title: "Começar é mais fácil do que arrumar o estoque.",
      steps: [
        "Crie sua conta em 60 segundos.",
        "Cadastre seus produtos e comece a registrar vendas.",
        "Acompanhe tudo no painel: estoque, vendas e clientes.",
      ],
    },
    social_proof: {
      title: "Lojas como a sua já estão vendendo mais.",
      testimonials: [
        {
          name: "Carla Mendes",
          role: "Dona da Boutique CM",
          content: "Antes eu perdia vendas por não saber o que tinha em estoque. Agora tenho controle total e minhas vendas aumentaram 40%!",
          rating: 5,
        },
        {
          name: "Roberto Silva",
          role: "Gerente de Multimarcas",
          content: "O sistema é muito fácil de usar. Minha equipe aprendeu em uma tarde e já estávamos vendendo com o PDV.",
          rating: 5,
        },
        {
          name: "Amanda Costa",
          role: "Proprietária de Brechó",
          content: "Consigo saber exatamente quais peças vendem mais e criar promoções certeiras. Meu faturamento dobrou em 6 meses.",
          rating: 5,
        },
      ],
    },
    pricing: {
      title: "Planos pensados para quem vende moda todos os dias.",
      subtitle: "Sem multa, sem fidelidade. Você fica porque faz sentido.",
      plans: [
        {
          name: "Essencial Loja Solo",
          price: "R$ 29",
          originalPrice: "R$ 49",
          description: "Para quem está começando",
          urgencyText: "Apenas 12 vagas neste valor",
          features: [
            "Até 500 produtos",
            "1 usuário",
            "PDV completo",
            "Controle de estoque",
            "50 mensagens WhatsApp/mês",
            "Relatórios básicos",
            "Suporte por e-mail",
          ],
        },
        {
          name: "Loja em Crescimento",
          price: "R$ 59",
          originalPrice: "R$ 99",
          description: "Para lojas em expansão • Inclui IA",
          urgencyText: "Oferta válida só hoje!",
          features: [
            "Até 2.000 produtos",
            "3 usuários",
            "🤖 Chatbot com IA no WhatsApp",
            "🤖 Copilot IA (assistente)",
            "PDV + Comissões automáticas",
            "Campanhas de marketing por IA",
            "Programa de fidelidade",
            "500 mensagens WhatsApp/mês",
            "Suporte prioritário",
          ],
          popular: true,
        },
        {
          name: "Rede de Lojas",
          price: "R$ 149",
          originalPrice: "R$ 199",
          description: "Para múltiplas unidades",
          features: [
            "Produtos ilimitados",
            "10 usuários",
            "🤖 IA Completa (todos módulos)",
            "🤖 Precificação inteligente por IA",
            "🤖 Scan de Notas Fiscais (OCR)",
            "Multilojas",
            "Relatórios avançados por IA",
            "Mensagens ilimitadas",
            "API de integração",
            "Suporte VIP 24/7",
          ],
        },
      ],
    },
    faq: {
      title: "Perguntas Frequentes",
      items: [
        {
          question: "Preciso de alguém de TI para usar?",
          answer: "Não! O sistema foi feito para ser simples. Você cria sua conta, cadastra seus produtos e já começa a usar. Se precisar de ajuda, nossa equipe está disponível para te orientar.",
        },
        {
          question: "Consigo importar produtos de planilha?",
          answer: "Sim! Você pode importar sua planilha de produtos (Excel ou CSV) em poucos cliques. O sistema reconhece automaticamente as colunas e você confere antes de importar.",
        },
        {
          question: "Posso cancelar quando quiser?",
          answer: "Claro! Não temos fidelidade. Você pode cancelar a qualquer momento, sem multas ou taxas. Seus dados ficam disponíveis por 30 dias após o cancelamento.",
        },
        {
          question: "Funciona no celular?",
          answer: "Sim! O sistema funciona perfeitamente no navegador do celular. Você pode registrar vendas, consultar estoque e ver relatórios de qualquer lugar.",
        },
        {
          question: "Emite nota fiscal?",
          answer: "Sim! Nos planos Pro e Premium, você pode emitir NFC-e e NFS-e diretamente pelo sistema, integrado com as principais prefeituras.",
        },
        {
          question: "Como funciona o suporte?",
          answer: "No plano básico, atendemos por e-mail em até 24h. Nos planos maiores, você tem chat em tempo real e até WhatsApp direto com nossa equipe.",
        },
      ],
    },
    cta_final: {
      title: "Cada dia sem controle é dinheiro que você deixa na mesa.",
      subtitle: "Comece agora e veja a diferença já no próximo mês.",
      button: "Quero organizar minha loja agora",
    },
    fomo: {
      activity_messages: [
        "Loja Glamour Boutique acabou de criar uma conta.",
        "Multimarcas Fashion Plus iniciou o teste gratuito há 3 minutos.",
        "Brechó Vintage Soul cadastrou 150 produtos agora.",
        "Boutique Elegance registrou 8 vendas hoje.",
        "Loja Trends acabou de ativar o módulo de estoque.",
        "Moda Center importou sua planilha de produtos.",
        "Style Store começou a usar o PDV há 2 minutos.",
        "Outlet Premium assinou o plano Pro agora mesmo.",
      ],
      offer_bar: {
        text: "🔥 Promoção de lançamento: 30% OFF no primeiro mês!",
        subtext: "⏰ Oferta válida apenas para as próximas 37 contas.",
        cta_text: "Garantir meu desconto",
      },
      exit_popup: {
        title: "Espere! Sua loja merece isso...",
        subtitle: "Você está prestes a deixar passar a melhor oportunidade de organizar seu negócio.",
        cta_text: "Quero meu desconto de 30%",
        discount_code: "MODA30",
      },
    },
    meta: {
      title: "Sistema para Lojas de Moda | Workia - Controle de Estoque e PDV",
      description: "Sistema completo para lojas de moda, boutiques e brechós. Controle de estoque, PDV rápido, gestão de clientes e relatórios. Teste grátis por 7 dias!",
    },
    theme: {
      primary: themes.moda.primary,
      secondary: themes.moda.secondary,
      accent: themes.moda.accent,
      gradient: themes.moda.gradient,
    },
    view_count_range: [12, 48],
  },
  beleza: {
    hero: {
      title: "Agenda Cheia. Zero Dor de Cabeça.",
      subtitle: "Um sistema simples que organiza seus horários, reduz faltas, fideliza clientes e mostra quanto você realmente está ganhando.",
      cta_primary: "Começar teste gratuito agora",
      cta_secondary: "Ver o painel em ação",
      highlight: "Ideal para salões, barbearias, estúdios e clínicas de estética.",
    },
    pain_solution: {
      pain_title: "Se você ainda controla tudo no caderno, você está perdendo dinheiro.",
      pains: [
        "Clientes esquecem o horário e não aparecem.",
        "Você perde tempo respondendo mensagem por mensagem no WhatsApp.",
        "Não sabe quem já veio, quem sumiu e quem mais compra de você.",
        "A grana entra, mas você não vê lucro no final do mês.",
      ],
      pain_subtitle: "Você trabalha muito, mas a gestão te sabota.",
      solution_title: "Organize sua agenda, automatize lembretes e acompanhe seus resultados.",
      solutions: [
        "Agenda inteligente com visual diário, semanal e por profissional.",
        "Lembretes automáticos por WhatsApp e e-mail para reduzir faltas.",
        "Histórico completo de cada cliente, com serviços, valores e preferências.",
        "Pacotes e sessões controlados automaticamente.",
      ],
    },
    benefits: {
      title: "Você cuida da beleza, nós cuidamos da gestão.",
      items: [
        "Veja o faturamento por profissional e por serviço.",
        "Controle seus produtos (tinturas, cremes, descartáveis) sem dor.",
        "Mostre antes e depois em uma galeria organizada por cliente.",
        "Crie campanhas para clientes que sumiram, com cupons de retorno.",
      ],
    },
    how_it_works: {
      title: "Começar é mais fácil do que lavar os pincéis.",
      steps: [
        "Crie sua conta e cadastre seus profissionais.",
        "Configure serviços, duração e valores.",
        "Comece a agendar e deixe o sistema trabalhar por você.",
      ],
    },
    social_proof: {
      title: "Do salão pequeno à clínica consolidada, todo mundo ganha em organização.",
      testimonials: [
        {
          name: "Patrícia Oliveira",
          role: "Dona do Salão Patrícia",
          content: "As faltas diminuíram 70% depois que ativei os lembretes automáticos. É incrível como algo tão simples fez tanta diferença!",
          rating: 5,
        },
        {
          name: "Carlos Eduardo",
          role: "Proprietário da Barbearia Premium",
          content: "Consigo ver quanto cada barbeiro faturou, controlar comissões e ainda tenho o histórico de cada cliente. Recomendo muito!",
          rating: 5,
        },
        {
          name: "Dra. Marina Santos",
          role: "Clínica de Estética Derma",
          content: "O controle de pacotes e sessões era um caos antes. Agora tudo está organizado e meus clientes adoram receber os lembretes.",
          rating: 5,
        },
      ],
    },
    pricing: {
      title: "Planos que cabem no bolso de qualquer profissional.",
      subtitle: "Escolha o ideal para o tamanho da sua equipe.",
      plans: [
        {
          name: "Profissional Solo",
          price: "R$ 49",
          description: "Para profissionais autônomos",
          features: [
            "1 profissional",
            "Agenda ilimitada",
            "Lembretes por WhatsApp",
            "Ficha de clientes",
            "50 mensagens/mês",
            "Relatórios básicos",
            "Suporte por e-mail",
          ],
        },
        {
          name: "Equipe Pequena",
          price: "R$ 99",
          description: "Para salões e barbearias • Inclui IA",
          features: [
            "Até 5 profissionais",
            "🤖 Chatbot IA no WhatsApp",
            "🤖 Copilot IA (assistente)",
            "Agenda por profissional",
            "Comissões automáticas",
            "Pacotes e sessões",
            "500 mensagens/mês",
            "Programa de fidelidade",
            "Suporte prioritário",
          ],
          popular: true,
        },
        {
          name: "Clínica Completa",
          price: "R$ 199",
          description: "Para clínicas e spas",
          features: [
            "Até 15 profissionais",
            "🤖 IA Completa (todos módulos)",
            "🤖 Análise de satisfação por IA",
            "Múltiplas unidades",
            "Prontuário digital",
            "Relatórios avançados",
            "Mensagens ilimitadas",
            "Galeria de evolução",
            "Suporte VIP 24/7",
          ],
        },
      ],
    },
    faq: {
      title: "Perguntas Frequentes",
      items: [
        {
          question: "Posso usar no celular?",
          answer: "Sim! O sistema funciona perfeitamente no navegador do celular. Você e sua equipe podem agendar, consultar clientes e ver relatórios de qualquer lugar.",
        },
        {
          question: "Os lembretes são automáticos mesmo?",
          answer: "Sim! Você configura uma vez e o sistema envia automaticamente por WhatsApp ou e-mail. Reduz faltas em até 70% sem você fazer nada.",
        },
        {
          question: "Consigo ter mais de uma unidade?",
          answer: "Sim! No plano Clínica Completa você pode gerenciar múltiplas unidades, com relatórios consolidados e separados por filial.",
        },
        {
          question: "Posso testar antes de pagar?",
          answer: "Claro! Todos os planos têm 7 dias grátis para você testar todas as funcionalidades. Sem precisar colocar cartão.",
        },
        {
          question: "Como funciona o controle de pacotes?",
          answer: "Você cadastra pacotes (ex: 10 sessões de depilação) e o sistema controla automaticamente quantas sessões cada cliente já usou e quantas faltam.",
        },
        {
          question: "Consigo controlar comissões da equipe?",
          answer: "Sim! Você define a porcentagem de cada profissional por serviço e o sistema calcula automaticamente quanto cada um tem a receber.",
        },
      ],
    },
    cta_final: {
      title: "Seu próximo mês pode ser o mais organizado da sua história.",
      subtitle: "Dê o primeiro passo agora.",
      button: "Quero organizar meu salão/clínica agora",
    },
    fomo: {
      activity_messages: [
        "Studio Glam acabou de criar uma conta.",
        "Barbearia Max iniciou o teste gratuito há 2 minutos.",
        "Clínica Bela Vita agendou 12 clientes hoje.",
        "Salão Elegance ativou lembretes automáticos.",
        "Espaço Zen registrou R$ 3.200 em serviços.",
        "Studio Hair configurou sua equipe de 5 profissionais.",
      ],
      offer_bar: {
        text: "💇 Plano especial para profissionais da beleza – Vagas limitadas!",
        subtext: "⚡ Apenas 42 vagas com desconto de 30%.",
        cta_text: "Aproveitar condição especial",
      },
      exit_popup: {
        title: "Não deixe suas clientes esperando!",
        subtitle: "Organize sua agenda hoje e reduza faltas em até 70%.",
        cta_text: "Quero meu desconto exclusivo",
        discount_code: "BELEZA30",
      },
    },
    meta: {
      title: "Sistema para Salões e Clínicas de Estética | Workia - Agenda Online",
      description: "Sistema completo para salões de beleza, barbearias e clínicas de estética. Agenda online, lembretes automáticos, controle de comissões. Teste grátis!",
    },
    theme: {
      primary: themes.beleza.primary,
      secondary: themes.beleza.secondary,
      accent: themes.beleza.accent,
      gradient: themes.beleza.gradient,
    },
    view_count_range: [18, 52],
  },
  servicos: {
    hero: {
      title: "Organize Seu Escritório. Reduza Erros. Ganhe Escala.",
      subtitle: "Centralize clientes, documentos, tarefas e prazos em um único sistema pensado para empresas de serviços.",
      cta_primary: "Testar gratuitamente agora",
      cta_secondary: "Ver tudo que o sistema faz",
      highlight: "Ideal para contabilidades, despachantes, consultorias e assessorias.",
    },
    pain_solution: {
      pain_title: "Seu escritório está perdendo produtividade.",
      pains: [
        "Prazos se perdem entre e-mails, WhatsApp e planilhas.",
        "Documentos importantes ficam espalhados em várias pastas.",
        "Você depende de pessoas específicas para lembrar de tudo.",
        "A equipe gasta mais tempo se achando do que entregando.",
      ],
      pain_subtitle: "Quanto mais clientes, maior o caos.",
      solution_title: "Centralize tudo e ganhe controle.",
      solutions: [
        "Dossiê completo de cada cliente, com documentos, tarefas e histórico.",
        "Biblioteca de arquivos com organização inteligente.",
        "Tarefas e checklists por processo, com responsáveis e prazos.",
        "Propostas comerciais com aprovação online e conversão em contratos.",
      ],
    },
    benefits: {
      title: "Seu escritório inteiro em uma única tela.",
      items: [
        "Clientes organizados e segmentados.",
        "Histórico de atendimentos acessível em segundos.",
        "Time alinhado com tarefas claras e checklists.",
        "Relatórios de produtividade e faturamento.",
      ],
    },
    how_it_works: {
      title: "Implementação simples, resultado imediato.",
      steps: [
        "Crie sua conta e convide sua equipe.",
        "Importe seus clientes e organize os documentos.",
        "Comece a usar tarefas, checklists e propostas.",
      ],
    },
    social_proof: {
      title: "Escritórios de todo o Brasil já estão mais organizados.",
      testimonials: [
        {
          name: "Dr. Marcelo Almeida",
          role: "Contador - Almeida Contabilidade",
          content: "Antes eu perdia horas procurando documentos. Agora tudo está organizado no dossiê de cada cliente. Ganhei tempo e profissionalismo.",
          rating: 5,
        },
        {
          name: "Fernanda Costa",
          role: "Proprietária - Despachante Express",
          content: "Os checklists de processos são incríveis. Minha equipe nunca mais esquece uma etapa. Os clientes percebem a diferença.",
          rating: 5,
        },
        {
          name: "Ricardo Santos",
          role: "Diretor - Consultoria RS",
          content: "As propostas comerciais ficaram muito mais profissionais. O cliente aprova online e já vira contrato. Fechamos 30% mais negócios.",
          rating: 5,
        },
      ],
    },
    pricing: {
      title: "Planos para escritórios de todos os tamanhos.",
      subtitle: "Do profissional autônomo à equipe consolidada.",
      plans: [
        {
          name: "Escritório Pequeno",
          price: "R$ 49",
          description: "Para profissionais individuais",
          features: [
            "Até 50 clientes",
            "1 usuário",
            "Dossiê de clientes",
            "Biblioteca de arquivos",
            "Tarefas e checklists",
            "50 mensagens/mês",
            "Suporte por e-mail",
          ],
        },
        {
          name: "Equipe em Crescimento",
          price: "R$ 99",
          description: "Para escritórios • Inclui IA",
          features: [
            "Até 200 clientes",
            "5 usuários",
            "🤖 Copilot IA (assistente)",
            "🤖 Propostas refinadas por IA",
            "Propostas comerciais",
            "Contratos digitais",
            "500 mensagens/mês",
            "Relatórios de produtividade",
            "Suporte prioritário",
          ],
          popular: true,
        },
        {
          name: "Operação Maior",
          price: "R$ 199",
          description: "Para escritórios consolidados",
          features: [
            "Clientes ilimitados",
            "15 usuários",
            "🤖 IA Completa (todos módulos)",
            "🤖 Scan de Notas Fiscais (OCR)",
            "Múltiplas unidades",
            "Automações avançadas",
            "Mensagens ilimitadas",
            "API de integração",
            "Suporte VIP 24/7",
          ],
        },
      ],
    },
    faq: {
      title: "Perguntas Frequentes",
      items: [
        {
          question: "Posso importar clientes de planilha?",
          answer: "Sim! Você pode importar sua base de clientes de Excel ou CSV em poucos cliques. O sistema mapeia automaticamente as colunas.",
        },
        {
          question: "É seguro armazenar documentos aqui?",
          answer: "Sim! Usamos criptografia de ponta e servidores seguros. Seus documentos estão protegidos e em conformidade com a LGPD.",
        },
        {
          question: "Consigo controlar usuários e permissões?",
          answer: "Sim! Você define quem pode ver, editar ou excluir cada tipo de informação. Controle total sobre o que cada colaborador acessa.",
        },
        {
          question: "Como funciona o sistema de propostas?",
          answer: "Você cria propostas personalizadas, envia o link para o cliente e ele aprova com um clique. A proposta pode virar contrato automaticamente.",
        },
        {
          question: "Posso criar processos padronizados?",
          answer: "Sim! Os checklists permitem criar processos com etapas definidas. Ideal para abertura de empresas, declarações, regularizações etc.",
        },
        {
          question: "Funciona para despachantes?",
          answer: "Perfeitamente! O sistema é ideal para controlar documentos, prazos, processos em andamento e comunicação com clientes.",
        },
      ],
    },
    cta_final: {
      title: "Mais organização, menos retrabalho.",
      subtitle: "Comece agora e sinta a diferença na primeira semana.",
      button: "Quero testar no meu escritório",
    },
    fomo: {
      activity_messages: [
        "Escritório Almeida & Lopes acabou de criar uma conta.",
        "Consultoria Nexus iniciou o teste gratuito há 5 minutos.",
        "Despachante Express cadastrou 45 clientes.",
        "Contabilidade Moderna ativou o módulo de contratos.",
        "Assessoria Prime enviou 3 propostas hoje.",
        "Escritório Futuro organizou sua biblioteca de documentos.",
      ],
      offer_bar: {
        text: "Condição especial para os 100 primeiros escritórios.",
        subtext: "Oferta por tempo limitado.",
        cta_text: "Garantir condição de fundador",
      },
      exit_popup: {
        title: "Não perca essa oportunidade!",
        subtitle: "Organize seu escritório e ganhe tempo.",
        cta_text: "Quero meu desconto",
        discount_code: "SERVICOS30",
      },
    },
    meta: {
      title: "Sistema para Escritórios e Consultorias | Workia - Gestão de Clientes",
      description: "Sistema completo para contabilidades, despachantes e consultorias. Gestão de clientes, documentos, propostas e contratos. Teste grátis por 7 dias!",
    },
    theme: {
      primary: themes.servicos.primary,
      secondary: themes.servicos.secondary,
      accent: themes.servicos.accent,
      gradient: themes.servicos.gradient,
    },
    view_count_range: [10, 30],
  },
  marketing: {
    hero: {
      title: "Sua Agência com Mais Resultado e Menos Caos.",
      subtitle: "Organize clientes, projetos, tarefas, arquivos e propostas em uma plataforma única, feita para quem vive de marketing.",
      cta_primary: "Criar conta agora",
      cta_secondary: "Ver como funciona na prática",
      highlight: "Ideal para agências, freelancers e times de social media.",
    },
    pain_solution: {
      pain_title: "Sua agência está perdendo tempo (e clientes).",
      pains: [
        "Clientes cobram retorno e você perde histórico de tudo.",
        "Demandas se perdem em grupos de WhatsApp.",
        "Arquivos ficam espalhados em Google Drive, e-mails e pastas locais.",
        "Você demora para responder propostas e perde contratos.",
      ],
      pain_subtitle: "Quanto mais clientes, maior a bagunça.",
      solution_title: "Centralize tudo e escale sua operação.",
      solutions: [
        "CRM com visão clara de cada cliente e projeto.",
        "Gestão de projetos e tarefas com Kanban e prazos.",
        "Biblioteca de mídia para criativos de campanhas.",
        "Orçamentos e propostas com aprovação online.",
      ],
    },
    benefits: {
      title: "A agência organizada que o cliente respeita.",
      items: [
        "Controle de horas, entregas e responsabilidades.",
        "Histórico de campanhas, resultados e ajustes.",
        "Automação de follow-up para leads e clientes.",
        "Relatórios profissionais para mostrar ao cliente.",
      ],
    },
    how_it_works: {
      title: "Comece em minutos, não em semanas.",
      steps: [
        "Crie sua conta e convide sua equipe.",
        "Cadastre seus clientes e projetos.",
        "Acompanhe tudo no painel: tarefas, prazos e resultados.",
      ],
    },
    social_proof: {
      title: "Agências de todo o Brasil já estão crescendo com mais controle.",
      testimonials: [
        {
          name: "Lucas Ferreira",
          role: "CEO - Agência PixelLab",
          content: "Antes a gente perdia muito tempo gerenciando por WhatsApp. Agora tudo está centralizado e meus clientes elogiam a organização.",
          rating: 5,
        },
        {
          name: "Juliana Alves",
          role: "Freelancer - Social Media",
          content: "Como freelancer, eu precisava parecer profissional. Com as propostas e relatórios do sistema, dobrei meus preços e os clientes aceitam.",
          rating: 5,
        },
        {
          name: "Pedro Henrique",
          role: "Sócio - Agência PH Marketing",
          content: "O Kanban de projetos salvou nossa operação. Sabemos exatamente o que cada pessoa está fazendo e nenhuma entrega atrasa.",
          rating: 5,
        },
      ],
    },
    pricing: {
      title: "Planos pensados para quem faz marketing acontecer.",
      subtitle: "Do freelancer à agência em escala.",
      plans: [
        {
          name: "Freelancer",
          price: "R$ 49",
          description: "Para profissionais independentes",
          features: [
            "Até 10 clientes",
            "1 usuário",
            "Projetos ilimitados",
            "Propostas comerciais",
            "Biblioteca de mídia",
            "50 mensagens/mês",
            "Suporte por e-mail",
          ],
        },
        {
          name: "Pequena Agência",
          price: "R$ 99",
          description: "Para equipes • Inclui IA",
          features: [
            "Até 30 clientes",
            "5 usuários",
            "🤖 Geração de campanhas por IA",
            "🤖 Copilot IA (assistente)",
            "Kanban de projetos",
            "Controle de horas",
            "500 mensagens/mês",
            "Relatórios de produtividade",
            "Suporte prioritário",
          ],
          popular: true,
        },
        {
          name: "Agência em Escala",
          price: "R$ 199",
          description: "Para operações maiores",
          features: [
            "Clientes ilimitados",
            "15 usuários",
            "🤖 IA Completa (todos módulos)",
            "🤖 Análise de concorrência por IA",
            "Múltiplas marcas",
            "Automações avançadas",
            "Mensagens ilimitadas",
            "API de integração",
            "Suporte VIP 24/7",
          ],
        },
      ],
    },
    faq: {
      title: "Perguntas Frequentes",
      items: [
        {
          question: "Consigo controlar várias marcas/clientes?",
          answer: "Sim! Cada cliente tem seu próprio espaço com projetos, arquivos, histórico e relatórios separados. Você alterna entre eles em um clique.",
        },
        {
          question: "Posso usar com minha equipe remota?",
          answer: "Perfeitamente! O sistema é 100% online. Sua equipe pode acessar de qualquer lugar, com controle de permissões por usuário.",
        },
        {
          question: "Tem limite de projetos?",
          answer: "Não! Em todos os planos você pode criar projetos ilimitados. O limite é apenas de clientes ativos.",
        },
        {
          question: "Como funciona a biblioteca de mídia?",
          answer: "Você organiza imagens, vídeos e documentos por cliente ou campanha. Ideal para ter tudo pronto na hora de criar conteúdo.",
        },
        {
          question: "Consigo ver quanto tempo gastei em cada projeto?",
          answer: "Sim! O controle de horas permite registrar tempo por tarefa e gerar relatórios para cobrar corretamente dos clientes.",
        },
        {
          question: "As propostas podem virar contratos?",
          answer: "Sim! Quando o cliente aprova a proposta, ela pode se transformar automaticamente em um contrato digital para assinatura.",
        },
      ],
    },
    cta_final: {
      title: "Sua agência não precisa de mais um grupo no WhatsApp. Precisa de um sistema.",
      subtitle: "Comece agora e transforme sua operação.",
      button: "Quero organizar minha operação agora",
    },
    fomo: {
      activity_messages: [
        "Agência PixelLab acabou de criar uma conta.",
        "Social Media Duda iniciou o teste gratuito há 4 minutos.",
        "Marketing360 cadastrou 8 clientes.",
        "Agência Impulso criou 5 projetos hoje.",
        "Creative Hub enviou uma proposta de R$ 12.000.",
        "Studio Digital organizou sua biblioteca de mídia.",
      ],
      offer_bar: {
        text: "Plano especial para agências nos primeiros 6 meses.",
        subtext: "Oferta por tempo limitado.",
        cta_text: "Aproveitar plano especial",
      },
      exit_popup: {
        title: "Não deixe seus projetos atrasarem!",
        subtitle: "Organize sua agência e entregue mais.",
        cta_text: "Quero meu desconto",
        discount_code: "MARKETING30",
      },
    },
    meta: {
      title: "Sistema para Agências de Marketing | Workia - Gestão de Projetos",
      description: "Sistema completo para agências de marketing e social media. Gestão de clientes, projetos, propostas e biblioteca de mídia. Teste grátis por 7 dias!",
    },
    theme: {
      primary: themes.marketing.primary,
      secondary: themes.marketing.secondary,
      accent: themes.marketing.accent,
      gradient: themes.marketing.gradient,
    },
    view_count_range: [20, 60],
  },
};

// Global site settings interface
export interface GlobalSiteSettings {
  app_name: string;
  site_title: string;
  site_description: string;
  site_keywords: string;
  support_email: string;
  support_phone: string;
  company_name: string;
  cnpj: string;
  address: string;
  social_links: {
    instagram: string;
    facebook: string;
    linkedin: string;
    youtube: string;
    whatsapp: string;
  };
  branding: {
    logo_url: string;
    favicon_url: string;
    og_image_url: string;
    primary_color: string;
    secondary_color: string;
  };
  email: {
    sender_name: string;
    sender_email: string;
  };
  trial_days: number;
  default_currency: string;
  footer_text: string;
  terms_url: string;
  privacy_url: string;
}

export const DEFAULT_GLOBAL_SETTINGS: GlobalSiteSettings = {
  app_name: "Work IA",
  site_title: "Work IA - Sistema de Gestão Inteligente",
  site_description: "Sistema completo de gestão para salões de beleza, barbearias, clínicas e comércios. Organize agenda, clientes, estoque e finanças em um só lugar.",
  site_keywords: "sistema de gestão, salão de beleza, barbearia, agendamento online, CRM, controle financeiro, estoque",
  support_email: "suporte@workia.com.br",
  support_phone: "(11) 99999-9999",
  company_name: "Workia Tecnologia LTDA",
  cnpj: "00.000.000/0001-00",
  address: "São Paulo, SP",
  social_links: {
    instagram: "https://instagram.com/workia",
    facebook: "https://facebook.com/workia",
    linkedin: "https://linkedin.com/company/workia",
    youtube: "https://youtube.com/@workia",
    whatsapp: "https://wa.me/5511999999999",
  },
  branding: {
    logo_url: "",
    favicon_url: "",
    og_image_url: "https://lovable.dev/opengraph-image-p98pqg.png",
    primary_color: "#3b82f6",
    secondary_color: "#1e293b",
  },
  email: {
    sender_name: "Workia",
    sender_email: "noreply@workia.com.br",
  },
  trial_days: 7,
  default_currency: "BRL",
  footer_text: "© 2024 Workia. Todos os direitos reservados.",
  terms_url: "/termos",
  privacy_url: "/privacidade",
};
